import { csv2json } from "json-2-csv"
import React, { useMemo, useRef, useState } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import {
    AccordionWithChildren,
    addModalThunk,
    delay,
    DropDownList,
    FormGroupWithCheckbox,
    getKeys,
    groupBy,
    InputLabel,
    StandardButtonWithSpinner,
    TextButton,
    useSelector,
} from "swiipe.portal.shared"
import { calculateTranslationValue, getTranslationItemId } from "../../../services/translationService"
import {
    ETranslationExportImportFileType,
    ITranslationChange,
    translationActions,
    translationSelectors,
} from "../../../store/reducers/translationSlice"
import {
    exportTranslationsThunk,
    syncTranslationChangesThunk,
    uploadTranslationChangesToServerThunk,
} from "../../../store/thunks/translationsThunks"
import useReduxDispatch from "../../../store/useReduxDispatch"
import { ITranslationExportFormat } from "../../../type/translations/ITranslationExportFormat"
import { ITranslationLanguageConfig } from "../../../type/translations/ITranslationLanguageConfig"
import { ITranslationLegacyImportFormat } from "../../../type/translations/ITranslationLegacyImportFormat"
import { getFileAsText, getFilesFromInput } from "../../../util/fileUtil"
import "./TranslationsActions.scss"
import TranslationsActionsDeepL from "./TranslationsActionsDeepL"

export interface ITranslationsActionsProps {
    languageConfigs: ITranslationLanguageConfig[]
}

const TranslationsActions = ({ languageConfigs }: ITranslationsActionsProps) => {
    const { t } = useTranslation()
    const dispatch = useReduxDispatch()
    const items = useSelector(translationSelectors.items) ?? []
    const groups = useMemo(() => groupBy(items, (i) => i.group), [items])
    const groupNames = useMemo(() => getKeys(groups), [groups])
    const itemsCount = items?.length || 0
    const selections = useSelector(translationSelectors.selections)
    const files = useSelector(translationSelectors.files)
    const currentLanguage = useSelector(translationSelectors.currentLanguage)
    const changes = useSelector(translationSelectors.changes)
    const selectedChanges = changes.filter((c) =>
        selections.some((s) => s.itemId === c.itemId && (!s.languages || s.languages.includes(c.language)))
    )

    const showMissingTranslationCounters = useSelector(translationSelectors.showMissingTranslationCounters)
    const showAddTags = useSelector(translationSelectors.showAddTags)
    const showCalculatedTags = useSelector(translationSelectors.showCalculatedTags)
    const showLastAddedTagSuggestion = useSelector(translationSelectors.showLastAddedTagSuggestion)
    const exportFileType = useSelector(translationSelectors.exportFileType)
    const [legacyImportNotFound, setLegacyImportNotFound] = useState<ITranslationLegacyImportFormat[]>([])
    const [exportGroup, setExportGroup] = useState("all")
    const [exportLanguage, setExportLanguage] = useState("all")

    const [includeCurrentTranslations, setIncludeCurrentTranslations] = useState(true)
    const [uploadOnlySelection, setUploadOnlySelection] = useState(true)
    const importFileInputRef = useRef<HTMLInputElement>(null)
    const importLegacyFileInputRef = useRef<HTMLInputElement>(null)

    const handleImport = async (numTotalImportLines: number, toChanges: ITranslationChange[]) => {
        const withChangedValues = toChanges.filter(
            (c) =>
                calculateTranslationValue(
                    items.find((i) => i.id === c.itemId),
                    changes,
                    c.language
                ) !== c.value
        )

        const batches = Math.ceil(withChangedValues.length / 1000)
        for (let i = 0; i < batches; i++) {
            await dispatch(translationActions.addChanges({ changes: withChangedValues.slice(1000 * i, 1000 * i + 1000) }))
            await delay(100)
        }
        await dispatch(syncTranslationChangesThunk())

        toast.success(
            `Imported ${withChangedValues.length} new translations.${
                withChangedValues.length !== toChanges.length
                    ? `\nSkipped ${toChanges.length - withChangedValues.length} without changes.`
                    : ""
            }${toChanges.length !== numTotalImportLines ? `\nSkipped ${numTotalImportLines - toChanges.length} blank.` : ""}`,
            {
                duration: 4000,
                position: "bottom-center",
            }
        )
    }

    return (
        <div className="translation-actions">
            <AccordionWithChildren leftAlignHeader header={<h4>Select translations</h4>}>
                <div className="translation-actions__selections">
                    {languageConfigs?.map((config) => {
                        const selectedItems = selections.filter((s) => !s.languages || s.languages.includes(config.language))
                        const relevantChanges = changes.filter((c) => c.language === config.language)
                        const deepLChanges = relevantChanges.filter((c) =>
                            c.allChanges?.some((a) => a.type === "TranslationService")
                        )
                        const missingAll = items.filter(
                            (item) => !calculateTranslationValue(item, relevantChanges, config.language)
                        )
                        const missingSelected = selectedItems.filter(
                            (selection) =>
                                !calculateTranslationValue(
                                    items.find((i) => i.id === selection.itemId),
                                    relevantChanges,
                                    config.language
                                )
                        )
                        const deepLSelected = selectedItems.filter((selection) =>
                            deepLChanges.some((c) => selection.itemId === c.itemId)
                        )
                        return (
                            <div className="translation-actions__selections-language-container" key={config.language}>
                                <div className="translation-actions__selections-language">{config.label}</div>
                                <div className="translation-actions__selections-counter translation-actions__selections-box">
                                    {selectedItems.length} / {itemsCount}{" "}
                                    <TextButton
                                        text="Select all"
                                        onClick={async () => {
                                            dispatch(
                                                translationActions.changeSelection({
                                                    language: config.language,
                                                    isSelected: true,
                                                })
                                            )
                                        }}
                                    />
                                </div>
                                <div className="translation-actions__missing-counter translation-actions__selections-box">
                                    {missingSelected.length} / {missingAll.length}{" "}
                                    <TextButton
                                        text="Select empty"
                                        textDecoration="underline"
                                        onClick={async () => {
                                            const itemsByGroup = groupBy(missingAll, (item) => item.group)
                                            getKeys(itemsByGroup).forEach((g) =>
                                                dispatch(
                                                    translationActions.changeSelection({
                                                        language: config.language,
                                                        isSelected: true,
                                                        keys: itemsByGroup[g].map((item) => item.key),
                                                        group: g,
                                                    })
                                                )
                                            )
                                        }}
                                    />
                                </div>
                                {
                                    <div className="translation-actions__translations-service-counter translation-actions__selections-box">
                                        {deepLSelected.length} / {deepLChanges.length}{" "}
                                        <TextButton
                                            text="Select DeepL"
                                            textDecoration="underline"
                                            onClick={async () => {
                                                const itemsByGroup = groupBy(deepLChanges, (item) => item.group)
                                                getKeys(itemsByGroup).forEach((g) =>
                                                    dispatch(
                                                        translationActions.changeSelection({
                                                            language: config.language,
                                                            isSelected: true,
                                                            keys: itemsByGroup[g].map((item) => item.key),
                                                            group: g,
                                                        })
                                                    )
                                                )
                                            }}
                                        />
                                    </div>
                                }
                                <TextButton
                                    className="translation-actions__selections-btn"
                                    text={"Deselect all"}
                                    textDecoration="underline"
                                    onClick={async () => {
                                        dispatch(
                                            translationActions.changeSelection({
                                                language: config.language,
                                                isSelected: false,
                                            })
                                        )
                                    }}
                                />
                            </div>
                        )
                    })}
                </div>
            </AccordionWithChildren>
            <AccordionWithChildren leftAlignHeader header={<h4>Settings</h4>}>
                <div className="translation-actions__row">
                    <InputLabel title="Active language">
                        <DropDownList
                            options={[
                                ...(languageConfigs.map((lc) => ({
                                    text: lc.label,
                                    value: lc.language,
                                })) ?? []),
                            ]}
                            onChange={(e) => {
                                dispatch(translationActions.setCurrentLanguage(e.currentTarget.value))
                            }}
                            value={currentLanguage}
                        />
                    </InputLabel>
                </div>
                <div className="translation-actions__row">
                    <FormGroupWithCheckbox
                        title="Show missing translation counters"
                        checked={showMissingTranslationCounters}
                        onChange={(e) => {
                            dispatch(translationActions.setShowMissingTranslationCounters(e.currentTarget.checked))
                        }}
                    />
                </div>
                <h5>Tags</h5>
                <div className="translation-actions__row">
                    <FormGroupWithCheckbox
                        title="Show add tags"
                        checked={showAddTags}
                        onChange={(e) => {
                            dispatch(translationActions.setShowAddTags(e.currentTarget.checked))
                        }}
                    />
                    <FormGroupWithCheckbox
                        title="Show calculated tags (own + inherited)"
                        checked={showCalculatedTags}
                        onChange={(e) => {
                            dispatch(translationActions.setShowCalculatedTags(e.currentTarget.checked))
                        }}
                    />
                </div>
                <div className="translation-actions__row">
                    <FormGroupWithCheckbox
                        title="Show last added suggestion"
                        checked={showLastAddedTagSuggestion}
                        onChange={(e) => {
                            dispatch(translationActions.setShowLastAddTagSuggestion(e.currentTarget.checked))
                        }}
                    />
                    <TextButton
                        text="Clear last added suggestion"
                        onClick={async () => {
                            dispatch(translationActions.setLastAddedTagName(undefined))
                        }}
                    />
                </div>
            </AccordionWithChildren>
            {changes.length > 0 && (
                <AccordionWithChildren leftAlignHeader header={<h4>Upload changes to Aera</h4>}>
                    <p>
                        <b>Files:</b> A new pull request will be created.
                        <br />
                        <b>Terms:</b> A new revision of the latest version will be created. Mark the latest revision as ready for
                        release in the test portal and release the changes from the production portal. <br />
                        <b>Emails and SMSes:</b> The latest version will be overwritten, but there will be a history. To release
                        the changes talk to IT. It requires a special tool. <br />
                        <b>Payment error messages:</b> The latest version will be overwritten, but there will be a history. To
                        release the changes talk to IT. It requires a manual DB migration.
                    </p>
                    <p>
                        There are currently {changes.length} {changes.length > 1 ? "changes" : "change"}.<br />
                        There are currently {selectedChanges.length} selected {selectedChanges.length > 1 ? "changes" : "change"}.
                    </p>
                    <FormGroupWithCheckbox
                        title="Upload only selection"
                        checked={uploadOnlySelection}
                        onChange={(e) => {
                            setUploadOnlySelection(e.currentTarget.checked)
                        }}
                    />
                    <StandardButtonWithSpinner
                        containerClass="translation-actions__action-button"
                        disabled={uploadOnlySelection ? selectedChanges.length === 0 : changes.length === 0}
                        onClick={async () => {
                            const res = await dispatch(
                                addModalThunk({
                                    type: "verify",
                                    text: "Are you sure you want to upload your changes?",
                                    acceptButtonText: "Upload",
                                })
                            )
                            if (res.type === "accepted") {
                                await dispatch(uploadTranslationChangesToServerThunk(uploadOnlySelection))
                            }
                        }}
                        title="Upload"
                    />
                </AccordionWithChildren>
            )}
            <TranslationsActionsDeepL languageConfigs={languageConfigs} />
            <AccordionWithChildren leftAlignHeader header={<h4>Export</h4>}>
                <div className="translation-actions__export">
                    <div className="translation-actions__row">
                        <InputLabel title="Export languages">
                            <DropDownList
                                options={[
                                    { text: "All", value: "all" },
                                    ...(languageConfigs.map((lc) => ({
                                        text: lc.label,
                                        value: lc.language,
                                    })) ?? []),
                                ]}
                                onChange={(e) => {
                                    setExportLanguage(e.currentTarget.value)
                                }}
                                value={exportLanguage}
                            />
                        </InputLabel>
                        <InputLabel title="Export file type" className="translation-actions__export-filetype-ddl">
                            <DropDownList<ETranslationExportImportFileType>
                                options={[
                                    {
                                        text: "JSON",
                                        value: "json",
                                    },
                                    { text: "CSV", value: "csv" },
                                ]}
                                value={exportFileType}
                                onChange={(e) => {
                                    dispatch(
                                        translationActions.setExportFileType(
                                            e.currentTarget.value as ETranslationExportImportFileType
                                        )
                                    )
                                }}
                            />
                        </InputLabel>
                    </div>
                    <div className="translation-actions__export-row">
                        <InputLabel title="Export group" className="translation-actions__export-group-ddl">
                            <DropDownList<string>
                                options={[
                                    {
                                        text: "All groups",
                                        value: "all",
                                    },
                                    ...getKeys(groups).map((groupName) => ({ text: groupName, value: groupName })),
                                ]}
                                value={exportGroup}
                                onChange={(e) => {
                                    setExportGroup(e.currentTarget.value)
                                }}
                            />
                        </InputLabel>
                    </div>
                    <FormGroupWithCheckbox
                        title="Include current translations"
                        checked={includeCurrentTranslations}
                        onChange={(e) => {
                            setIncludeCurrentTranslations(e.currentTarget.checked)
                        }}
                    />
                    <StandardButtonWithSpinner
                        containerClass="translation-actions__action-button"
                        onClick={async () => {
                            await delay(300)
                            if (exportLanguage === "all") {
                                for (let i = 0; i < languageConfigs.length; i++) {
                                    await dispatch(
                                        exportTranslationsThunk(exportFileType, languageConfigs[i].language, {
                                            includeCurrentTranslations: includeCurrentTranslations,
                                            group: exportGroup === "all" ? undefined : exportGroup,
                                        })
                                    )
                                }
                            } else {
                                await dispatch(
                                    exportTranslationsThunk(exportFileType, exportLanguage, {
                                        includeCurrentTranslations: includeCurrentTranslations,
                                        group: exportGroup === "all" ? undefined : exportGroup,
                                    })
                                )
                            }
                        }}
                        title="Export"
                    />
                </div>
            </AccordionWithChildren>

            <AccordionWithChildren leftAlignHeader header={<h4>Import</h4>}>
                <div className="translation-actions__import">
                    <div className="translation-actions__row">
                        <InputLabel title="Import files" className="translation-actions__import-file-label">
                            <input
                                ref={importFileInputRef}
                                className="translation-actions__import-file-input"
                                type="file"
                                accept=".json,.csv"
                                multiple
                            ></input>
                        </InputLabel>
                    </div>
                    <StandardButtonWithSpinner
                        title="Import"
                        isSmall
                        containerClass="translation-actions__action-button"
                        onClick={async () => {
                            await delay(300)
                            const files = getFilesFromInput(importFileInputRef.current)
                            if (files.length === 0) {
                                toast.error("No files selected", { position: "bottom-center" })
                            }

                            const fileContents: ITranslationExportFormat[] = (
                                await Promise.all(
                                    files.map(async (f) => {
                                        let content = (await getFileAsText(f)) as string
                                        if (f.name.toLowerCase().endsWith(".json")) {
                                            return JSON.parse(content) as ITranslationExportFormat[]
                                        } else {
                                            if (content.startsWith("Column")) {
                                                // Excel added own default column names on top of header row - remove them
                                                const newLine = content.indexOf("\r\n") >= 0 ? "\r\n" : "\n"
                                                content = content.split(newLine).slice(1).join(newLine)
                                            }

                                            let importData = csv2json(content, {}) as ITranslationExportFormat[]
                                            // If wrong csv format, then each row will have only 1 property
                                            const isValidImport = () => importData.length > 0 && getKeys(importData[0]).length > 4
                                            if (!isValidImport()) {
                                                importData = csv2json(content, {
                                                    delimiter: { field: ";", eol: "\r\n", wrap: '"' },
                                                }) as ITranslationExportFormat[]
                                            }
                                            return isValidImport() ? importData : []
                                        }
                                    })
                                )
                            ).flat()

                            const withValues = fileContents.filter((fc) => !!fc.toValue)
                            const toChanges = withValues.map<ITranslationChange>((change) => ({
                                group: change.group,
                                key: change.key,
                                itemId: getTranslationItemId(change.group, change.key),
                                language:
                                    languageConfigs.find((lc) => lc.shortCulture === change.toLanguageCode)?.language ??
                                    change.toLanguageCode,
                                value:
                                    change.toValue === null || change.toValue === undefined
                                        ? change.toValue
                                        : change.toValue + "",
                                type: "Import",
                                created: new Date().toISOString(),
                            }))
                            await handleImport(fileContents.length, toChanges)
                        }}
                    />
                    <StandardButtonWithSpinner
                        containerClass="translation-actions__action-button mt-3"
                        isDelete
                        onClick={async () => {
                            const itemsToClear = items.filter((i) => selections.some((si) => si.itemId === i.id))
                            dispatch(
                                translationActions.clearChanges({
                                    itemIds: itemsToClear.map((i) => i.id),
                                    language: undefined,
                                    type: "Import",
                                })
                            )

                            dispatch(syncTranslationChangesThunk())
                        }}
                        title="Clear imports for selection"
                    />
                </div>
            </AccordionWithChildren>
            <AccordionWithChildren leftAlignHeader header={<h4>Legacy Import</h4>}>
                <div className="translation-actions__legacy-import">
                    <div className="translation-actions__row">
                        <InputLabel title="Import legacy Files" className="translation-actions__import-file-label">
                            <input
                                ref={importLegacyFileInputRef}
                                className="translation-actions__import-file-input"
                                type="file"
                                accept=".json,.csv"
                                multiple
                            ></input>
                        </InputLabel>
                    </div>
                    {legacyImportNotFound.length > 0 && (
                        <div className="translation-actions__row translation-actions__legacy-import-not-found-container">
                            <b>Not found:</b>
                            <div>
                                {legacyImportNotFound.map((row, index) => {
                                    return <div key={index}>{row["Dev key"]}</div>
                                })}
                            </div>
                        </div>
                    )}
                    <StandardButtonWithSpinner
                        title="Import Legacy File"
                        isSmall
                        containerClass="translation-actions__action-button"
                        onClick={async () => {
                            setLegacyImportNotFound([])
                            await delay(300)
                            const uploadedFiles = getFilesFromInput(importLegacyFileInputRef.current)
                            if (uploadedFiles.length === 0) {
                                toast.error("No file selected", { position: "bottom-center" })
                            }

                            const fileContents: ITranslationLegacyImportFormat[] = (
                                await Promise.all(
                                    uploadedFiles.map(async (f) => {
                                        const content = (await getFileAsText(f)) as string
                                        if (f.name.toLowerCase().endsWith(".json")) {
                                            return JSON.parse(content) as ITranslationLegacyImportFormat[]
                                        } else {
                                            return csv2json(content, {
                                                delimiter: { field: ";", eol: "\r\n", wrap: '"' },
                                            }) as ITranslationLegacyImportFormat[]
                                        }
                                    })
                                )
                            ).flat()

                            const danish = languageConfigs.find((lc) => lc.shortCulture === "da")!.language
                            const english = languageConfigs.find((lc) => lc.shortCulture === "en")!.language
                            const swedish = languageConfigs.find((lc) => lc.shortCulture === "sv")!.language
                            const norwegian = languageConfigs.find((lc) => lc.shortCulture === "no")!.language
                            const french = languageConfigs.find((lc) => lc.shortCulture === "fr")!.language
                            const dutch = languageConfigs.find((lc) => lc.shortCulture === "nl")!.language

                            const result = fileContents.reduce(
                                (res, row) => {
                                    const itemsForRow = items.filter((i) => i.key === row["Dev key"])
                                    if (itemsForRow.length === 0) {
                                        res.notFound.push(row)
                                        return res
                                    }
                                    if (itemsForRow.length > 1) {
                                        res.ambiguous.push({
                                            count: itemsForRow.length,
                                            row,
                                        })
                                    }
                                    itemsForRow.forEach((itemForRow) => {
                                        const pushLanguage = (getVal: () => string, language: string) => {
                                            const val = getVal()
                                            if (val) {
                                                res.changes.push({
                                                    group: itemForRow.group,
                                                    key: itemForRow.key,
                                                    itemId: getTranslationItemId(itemForRow.group, itemForRow.key),
                                                    language: language,
                                                    value: val + "",
                                                    type: "Import",
                                                    created: new Date().toISOString(),
                                                })
                                            }
                                        }
                                        pushLanguage(() => row.Danish, danish)
                                        pushLanguage(() => row.English, english)
                                        pushLanguage(() => row.Swedish, swedish)
                                        pushLanguage(() => row.Norwegian, norwegian)
                                        pushLanguage(() => row.French, french)
                                        pushLanguage(() => row.Dutch, dutch)
                                    })

                                    return res
                                },
                                { changes: [], ambiguous: [], notFound: [] } as {
                                    changes: ITranslationChange[]
                                    ambiguous: { count: number; row: ITranslationLegacyImportFormat }[]
                                    notFound: ITranslationLegacyImportFormat[]
                                }
                            )

                            setLegacyImportNotFound(result.notFound)
                            await handleImport(items.length, result.changes)
                        }}
                    />
                    <StandardButtonWithSpinner
                        containerClass="translation-actions__action-button mt-3"
                        isDelete
                        onClick={async () => {
                            const itemsToClear = items.filter((i) => selections.some((si) => si.itemId === i.id))
                            await dispatch(
                                translationActions.clearChanges({
                                    itemIds: itemsToClear.map((i) => i.id),
                                    language: undefined,
                                    type: "Import",
                                })
                            )

                            dispatch(syncTranslationChangesThunk())
                        }}
                        title="Clear imports for selection"
                    />
                </div>
            </AccordionWithChildren>
        </div>
    )
}

export default TranslationsActions
